import type { ReactElement } from "react";

import AbstractingBlockchainGasFees from "~/_posts/abstracting-blockchain-gas-fees.mdx";
import AnnouncingSupportForArbitrum from "~/_posts/announcing-support-for-arbitrum.mdx";
import AnnouncingSupportForBase from "~/_posts/announcing-support-for-base.mdx";
import AnnouncingSupportForCantonNetwork from "~/_posts/announcing-support-for-canton-network.mdx";
import AnnouncingSupportForCelo from "~/_posts/announcing-support-for-celo.mdx";
import AnnouncingSupportForEthereumClassic from "~/_posts/announcing-support-for-ethereum-classic.mdx";
import AnnouncingSupportForHedera from "~/_posts/announcing-support-for-hedera.mdx";
import AnnouncingSupportForOptimism from "~/_posts/announcing-support-for-optimism.mdx";
import AnnouncingSupportForSolana from "~/_posts/announcing-support-for-solana.mdx";
import AnnouncingSupportForStellar from "~/_posts/announcing-support-for-stellar.mdx";
import AnnouncingSupportForUSDCOnBaseArbitrumCelo from "~/_posts/announcing-support-for-usdc-on-base-arbitrum-celo.mdx";
import AnnouncingSupportForUSDP from "~/_posts/announcing-support-for-usdp.mdx";
import Attestations from "~/_posts/attestations.mdx";
import BaseBeta from "~/_posts/base-beta.mdx";
import BitGoStablecoinSupport from "~/_posts/bitgo-stablecoin-support.mdx";
import BlockchainSpeedAndCostComparisons from "~/_posts/blockchain-speed-and-cost-comparisons.mdx";
import BlockchainSpeedCostComparisons from "~/_posts/blockchain-speed-cost-comparisons.mdx";
import BraleBase from "~/_posts/brale-base.mdx";
import BraleEcosystem from "~/_posts/brale-ecosystem.mdx";
import BraleFundraising from "~/_posts/brale-fundraising.mdx";
import CitizenWallet from "~/_posts/citizen-wallet.mdx";
import CoinflowRealtimeStablecoinPayouts from "~/_posts/coinflow-realtime-stablecoin-payouts.mdx";
import Context from "~/_posts/context.mdx";
import CostsToSend1mOnLeadingBlockchains from "~/_posts/costs-to-send-1m-on-leading-blockchains.mdx";
import CostsToTransfer1mOnChain from "~/_posts/costs-to-transfer-1m-on-chain.mdx";
import DemystifyingOnrampsAndOfframps from "~/_posts/demystifying-onramps-and-offramps.mdx";
import HowToLaunchAStablecoin from "~/_posts/how-to-launch-a-stablecoin.mdx";
import IntroducingBraleBusinessAccounts from "~/_posts/introducing-brale-business-accounts.mdx";
import IntroducingClassicUSD from "~/_posts/introducing-classic-usd.mdx";
import IntroducingMXNe from "~/_posts/introducing-mxne.mdx";
import IntroducingStableCoinSBC from "~/_posts/introducing-stable-coin-sbc.mdx";
import IntroducingStablecoinIssuanceAPI from "~/_posts/introducing-stablecoin-issuance-api.mdx";
import IntroducingTreasury from "~/_posts/introducing-treasury.mdx";
import LaunchingStablecoinsOnAlchemyRollups from "~/_posts/launching-stablecoins-on-alchemy-rollups.mdx";
import MintAndRedeemStablecoinsWithACHViaPlaid from "~/_posts/mint-and-redeem-stablecoins-with-ach-via-plaid.mdx";
import MPCStablecoinVault from "~/_posts/mpc-stablecoin-vault.mdx";
import OctoberConferencesForBrale from "~/_posts/october-conferences-for-brale.mdx";
import OurApproachToAttestations from "~/_posts/our-approach-to-attestations.mdx";
import OurQ3HiringCohort from "~/_posts/our-q3-hiring-cohort.mdx";
import RainCards from "~/_posts/rain-cards.mdx";
import SendStablecoinsByEmail from "~/_posts/send-stablecoins-by-email.mdx";
import StableCoinSBCWhitePaper from "~/_posts/stable-coin-sbc-white-paper.mdx";
import StablecoinIssuanceAPI from "~/_posts/stablecoin-issuance-api.mdx";
import StablecoinIssuanceCLI from "~/_posts/stablecoin-issuance-cli.mdx";
import StablecoinMicropayments from "~/_posts/stablecoin-micropayments.mdx";
import StablecoinTokenStandardsOnLeadingBlockchains from "~/_posts/stablecoin-token-standards-on-leading-blockchains.mdx";
import TheStablecoinOnramp from "~/_posts/the-stablecoin-on-ramp.mdx";
import ToolsForTesting from "~/_posts/tools-for-testing-bespoke-stablecoins-or-digital-assets.mdx";
import TreasuryBeta from "~/_posts/treasury-beta.mdx";
import PrivatePaymentBlockchain from "~/_posts/understanding-private-blockchains.mdx";
import WeAreBrale from "~/_posts/we-are-brale.mdx";
import { Contributors } from "~/components/blog/contributors";
import { CoverImage } from "~/components/blog/cover-image";
import { SocialDiscussions } from "~/components/blog/social-discussions";
import { CallToAction } from "~/components/site/call-to-action";
import type { Author } from "~/lib/authors";
import type { BlogPost } from "~/lib/constants";
import type { CTA, Image } from "~/lib/post";

const SLUG_TO_COMPONENT: Record<BlogPost, ReactElement> = {
  "announcing-support-for-hedera": <AnnouncingSupportForHedera />,
  "launching-stablecoins-on-alchemy-rollups": (
    <LaunchingStablecoinsOnAlchemyRollups />
  ),
  "october-conferences-for-brale": <OctoberConferencesForBrale />,
  "stablecoin-token-standards-on-leading-blockchains": (
    <StablecoinTokenStandardsOnLeadingBlockchains />
  ),
  "stablecoin-micropayments": <StablecoinMicropayments />,
  "announcing-support-for-usdc-on-base-arbitrum-celo": (
    <AnnouncingSupportForUSDCOnBaseArbitrumCelo />
  ),
  "introducing-brale-business-accounts": <IntroducingBraleBusinessAccounts />,
  "costs-to-send-1m-on-leading-blockchains": (
    <CostsToSend1mOnLeadingBlockchains />
  ),
  "our-q3-hiring-cohort": <OurQ3HiringCohort />,
  "announcing-support-for-canton-network": (
    <AnnouncingSupportForCantonNetwork />
  ),
  "mint-and-redeem-stablecoins-with-ach-via-plaid": (
    <MintAndRedeemStablecoinsWithACHViaPlaid />
  ),
  "introducing-mxne": <IntroducingMXNe />,
  "introducing-classic-usd": <IntroducingClassicUSD />,
  "demystifying-onramps-and-offramps": <DemystifyingOnrampsAndOfframps />,
  "announcing-support-for-usdp": <AnnouncingSupportForUSDP />,
  "announcing-support-for-ethereum-classic": (
    <AnnouncingSupportForEthereumClassic />
  ),
  "the-stablecoin-on-ramp": <TheStablecoinOnramp />,
  "how-to-launch-a-stablecoin": <HowToLaunchAStablecoin />,
  "bitgo-stablecoin-support": <BitGoStablecoinSupport />,
  "stablecoin-issuance-cli": <StablecoinIssuanceCLI />,
  "send-stablecoins-by-email": <SendStablecoinsByEmail />,
  "stablecoin-issuance-api": <StablecoinIssuanceAPI />,
  "announcing-support-for-arbitrum": <AnnouncingSupportForArbitrum />,
  "costs-to-transfer-1m-on-chain": <CostsToTransfer1mOnChain />,
  "coinflow-realtime-stablecoin-payouts": <CoinflowRealtimeStablecoinPayouts />,
  attestations: <Attestations />,
  "introducing-stablecoin-issuance-api": <IntroducingStablecoinIssuanceAPI />,
  "mpc-stablecoin-vault": <MPCStablecoinVault />,
  "rain-cards": <RainCards />,
  "announcing-support-for-solana": <AnnouncingSupportForSolana />,
  "citizen-wallet": <CitizenWallet />,
  "brale-ecosystem": <BraleEcosystem />,
  "treasury-beta": <TreasuryBeta />,
  "blockchain-speed-cost-comparisons": <BlockchainSpeedCostComparisons />,
  "announcing-support-for-optimism": <AnnouncingSupportForOptimism />,
  "announcing-support-for-base": <AnnouncingSupportForBase />,
  "announcing-support-for-stellar": <AnnouncingSupportForStellar />,
  "our-approach-to-attestations": <OurApproachToAttestations />,
  "introducing-treasury": <IntroducingTreasury />,
  "announcing-support-for-celo": <AnnouncingSupportForCelo />,
  "tools-for-testing-bespoke-stablecoins-or-digital-assets": (
    <ToolsForTesting />
  ),
  "base-beta": <BaseBeta />,
  "stable-coin-sbc-white-paper": <StableCoinSBCWhitePaper />,
  "introducing-stable-coin-sbc": <IntroducingStableCoinSBC />,
  "brale-fundraising": <BraleFundraising />,
  "understanding-private-blockchains": <PrivatePaymentBlockchain />,
  "abstracting-blockchain-gas-fees": <AbstractingBlockchainGasFees />,
  "brale-base": <BraleBase />,
  "blockchain-speed-and-cost-comparisons": (
    <BlockchainSpeedAndCostComparisons />
  ),
  context: <Context />,
  "we-are-brale": <WeAreBrale />,
};

type Props = Readonly<{
  authors: readonly Author[];
  coverImage?: Image;
  slug: BlogPost;
  discussTwitter?: string;
  discussLinkedIn?: string;
  discussFarcaster?: string;
  callToAction?: CTA;
}>;

export function PostBody({
  authors,
  coverImage,
  slug,
  discussLinkedIn,
  discussTwitter,
  discussFarcaster,
  callToAction,
}: Props): JSX.Element {
  return (
    <div>
      {coverImage && (
        <div className="mb-10 lg:mt-0 lg:mb-12 sm:mx-0">
          <CoverImage
            image={coverImage}
            link={coverImage.link}
            className="bg-white dark:bg-black round border border-primary"
          />
        </div>
      )}
      <div className="prose dark:prose-invert max-w-none">
        {SLUG_TO_COMPONENT[slug]}
      </div>
      {callToAction && (
        <CallToAction
          seoRef={slug}
          heading={callToAction.heading}
          subheading={callToAction.subheading}
          button={callToAction.button}
          ctaUrl={callToAction.ctaUrl}
          externalUrl={callToAction.externalUrl}
        />
      )}
      <SocialDiscussions
        twitterUrl={discussTwitter}
        linkedInUrl={discussLinkedIn}
        farcasterUrl={discussFarcaster}
      />
      <Contributors authors={authors} />
    </div>
  );
}
